<template>
  <div class="product-catalog site__centered">
    <h2 class="product-catalog__title">Shop</h2>

    <div class="product-catalog__body">
      <SideNav />

      <div class="product-catalog__content">
        <div v-if="isLoading" class="text-center py-5">
          <b-spinner variant="primary" />
        </div>

        <!-- Empty state -->
        <div v-else-if="!catalogPrograms.length" class="text-center py-5">
          <p class="text-muted">No products available at this time.</p>
        </div>

        <!-- Program accordions -->
        <div v-else>
          <div
            v-for="(programEntry, pIdx) in catalogPrograms"
            :key="programEntry.program.id"
            class="pc-program"
          >
            <!-- Program header (accordion toggle) -->
            <div
              class="pc-program__header"
              :class="{ 'pc-program__header--open': openPrograms[pIdx] }"
              @click="toggleProgram(pIdx)"
            >
              <img
                v-if="programEntry.program.logo"
                :src="programEntry.program.logo"
                class="pc-program__logo"
                :alt="programEntry.program.name"
              />
              <span class="pc-program__name">{{ programEntry.program.name }}</span>
              <span v-if="programEntry.program.date_start" class="pc-program__date">
                {{ formatDate(programEntry.program.date_start) }}
              </span>
              <span class="pc-program__chevron">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 6l4 4 4-4"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </div>

            <!-- Program body -->
            <div v-show="openPrograms[pIdx]" class="pc-program__body">
              <div
                v-for="(catEntry, cIdx) in programEntry.categories"
                :key="catEntry.category ? catEntry.category.id : 'uncategorized-' + pIdx"
                class="pc-category-card my-applications__border"
              >
                <!-- Category card header (mimics ApplicationItem) -->
                <div class="pc-category-card__header">
                  <div class="pc-category-card__info">
                    <span class="pc-category-card__name">
                      {{ catEntry.category ? catEntry.category.name : 'Other Products' }}
                    </span>
                    <span v-if="catEntry.category && catEntry.category.description" class="pc-category-card__desc">
                      {{ catEntry.category.description }}
                    </span>
                  </div>
                  <button
                    class="pc-category-card__toggle btn btn--text"
                    @click="toggleCategory(pIdx, cIdx)"
                  >
                    <span>{{ openCategories[pIdx] && openCategories[pIdx][cIdx] ? 'Hide' : 'View' }} products</span>
                    <svg
                      class="pc-category-card__chevron"
                      :class="{ 'pc-category-card__chevron--open': openCategories[pIdx] && openCategories[pIdx][cIdx] }"
                      width="14" height="14" viewBox="0 0 16 16" fill="none"
                    >
                      <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </button>
                </div>

                <!-- Products accordion -->
                <div v-show="openCategories[pIdx] && openCategories[pIdx][cIdx]" class="pc-products">
                  <div
                    v-for="product in catEntry.products"
                    :key="product.uuid"
                    class="pc-product-row"
                    :class="{ 'pc-product-row--closed': !product.is_open }"
                  >
                    <div class="pc-product-row__info">
                      <img
                        v-if="product.image"
                        :src="product.image"
                        :alt="product.name"
                        class="pc-product-row__image"
                      />
                      <div class="pc-product-row__text">
                        <span class="pc-product-row__name">{{ product.name }}</span>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span v-if="product.description" class="pc-product-row__desc" v-html="product.description" />
                        <span v-if="!product.is_open" class="pc-product-row__badge">Currently Unavailable</span>
                      </div>
                    </div>
                    <b-button
                      variant="primary"
                      size="sm"
                      class="pc-product-row__btn"
                      :disabled="!product.is_open"
                      @click="openCheckout(product)"
                    >
                      Purchase
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Checkout modal -->
    <ProductCheckout />
    <!-- Shared modals needed by the checkout flow -->
    <AddNewCard />
    <AddNewBankAccount />
    <StatusModal />
  </div>
</template>

<script>
import { BSpinner, BButton } from 'bootstrap-vue'
import SideNav from '@/components/site/SideNav.vue'
import ProductCheckout from './ProductCheckout.vue'
import AddNewCard from '@/views/PaymentPortal/Modals/AddNewCard/AddNewCard.vue'
import AddNewBankAccount from '@/views/PaymentPortal/Modals/AddNewCard/AddNewBankAccount.vue'
import StatusModal from '@/views/PaymentPortal/Modals/Status/StatusModal.vue'

export default {
  name: 'ProductCatalog',
  components: {
    SideNav,
    BSpinner,
    BButton,
    ProductCheckout,
    AddNewCard,
    AddNewBankAccount,
    StatusModal,
  },

  data() {
    return {
      /** Track which program accordions are open: { pIdx: true/false } */
      openPrograms: {},
      /** Track which category accordions are open: { pIdx: { cIdx: true/false } } */
      openCategories: {},
    }
  },

  computed: {
    isLoading() {
      return this.$store.getters['productCatalog/getIsLoading']
    },
    catalogPrograms() {
      return this.$store.getters['productCatalog/getCatalogPrograms']
    },
  },

  watch: {
    catalogPrograms(programs) {
      // Auto-expand the first program when data loads
      if (programs.length && !Object.keys(this.openPrograms).length) {
        this.$set(this.openPrograms, 0, true)
      }
    },
  },

  created() {
    this.$store.dispatch('productCatalog/fetchCatalog')
  },

  methods: {
    toggleProgram(pIdx) {
      this.$set(this.openPrograms, pIdx, !this.openPrograms[pIdx])
    },

    toggleCategory(pIdx, cIdx) {
      if (!this.openCategories[pIdx]) {
        this.$set(this.openCategories, pIdx, {})
      }
      this.$set(
        this.openCategories[pIdx],
        cIdx,
        !this.openCategories[pIdx][cIdx],
      )
    },

    openCheckout(product) {
      this.$store.dispatch('productCatalog/openCheckout', product)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
  },
}
</script>

<style lang="scss" scoped>
/* ─── Page shell ──────────────────────────────────────── */
.product-catalog {
  &__title {
    margin-bottom: 24px;
    font-size: 24px;
    font-weight: 700;
  }

  &__body {
    display: flex;
    gap: 24px;
    align-items: flex-start;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }
}

/* ─── Program accordion ───────────────────────────────── */
.pc-program {
  margin-bottom: 16px;
  border: 1px solid #eceff9;
  border-radius: 8px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    cursor: pointer;
    background: #f8f9fd;
    transition: background 0.15s;
    user-select: none;

    &:hover {
      background: #eef0fb;
    }

    &--open {
      background: #eef0fb;
    }
  }

  &__logo {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__name {
    font-weight: 700;
    font-size: 16px;
    flex: 1;
    color: #3d4361;
  }

  &__date {
    font-size: 13px;
    color: #8a92a6;
    white-space: nowrap;
  }

  &__chevron {
    color: #7367f0;
    flex-shrink: 0;
    transition: transform 0.2s;
  }

  &__header--open &__chevron {
    transform: rotate(180deg);
  }

  &__body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #fff;
  }
}

/* ─── Category card (mimics ApplicationItem) ──────────── */
/* reuses .my-applications__border from the global SCSS */
.pc-category-card {
  border-radius: 8px;
  overflow: hidden;

  &__header {
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-weight: 700;
    font-size: 15px;
    color: #3d4361;
  }

  &__desc {
    font-size: 13px;
    color: #8a92a6;
    margin-top: 2px;
  }

  &__toggle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #7367f0;
    background: none;
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: #f0eeff;
    }
  }

  &__chevron {
    transition: transform 0.2s;
    color: #7367f0;

    &--open {
      transform: rotate(180deg);
    }
  }
}

/* ─── Products list ───────────────────────────────────── */
.pc-products {
  border-top: 1px solid #eceff9;
}

.pc-product-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f4f5fa;
  background: #fff;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafbff;
  }

  &--closed {
    opacity: 0.6;
  }

  &__info {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__image {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-weight: 600;
    font-size: 14px;
    color: #3d4361;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__desc {
    font-size: 12px;
    color: #8a92a6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 400px;

    // strip any block-level margin from injected HTML
    ::v-deep p {
      margin: 0;
      display: inline;
    }
  }

  &__badge {
    display: inline-block;
    font-size: 11px;
    color: #888;
    background: #f0f0f0;
    border-radius: 3px;
    padding: 1px 6px;
    margin-top: 3px;
  }

  &__btn {
    flex-shrink: 0;
  }
}

@media (max-width: 767px) {
  .product-catalog__body {
    flex-direction: column;
  }

  .pc-product-row {
    flex-wrap: wrap;

    &__btn {
      width: 100%;
      margin-top: 8px;
    }
  }
}
</style>

